import type { ReactNode } from 'react';
import { Symbol } from './symbol';

interface EmptyStateProps {
  /** Material Symbols ligature for the round accent-soft art. */
  icon?: string;
  title: string;
  /** What will make this fill up, or what to do next. */
  body?: ReactNode;
  action?: ReactNode;
  /** `bare` drops the panel chrome, for use inside an existing card/table. */
  bare?: boolean;
  className?: string;
}

/** Design system v2 §11 empty state: say why it's empty and what to do. */
export function EmptyState({ icon = 'inbox', title, body, action, bare = false, className = '' }: EmptyStateProps) {
  return (
    <div
      className={[
        'flex flex-col items-center gap-2.5 px-6 py-10 text-center',
        bare ? '' : 'panel',
        className,
      ].join(' ')}
    >
      <div className="mb-1.5 grid h-[72px] w-[72px] place-items-center rounded-full bg-admin-soft text-admin-ink">
        <Symbol name={icon} size={32} weight={400} />
      </div>
      <h4 className="text-[17px] font-semibold text-ink-900">{title}</h4>
      {body && <p className="max-w-[40ch] text-[14px] text-ink-500">{body}</p>}
      {action && <div className="mt-1.5">{action}</div>}
    </div>
  );
}

interface ChartEmptyProps {
  icon?: string;
  title: string;
  body?: ReactNode;
  action?: ReactNode;
  className?: string;
}

/** Empty chart: ruled background plus when the data will appear (§04c). */
export function ChartEmpty({ icon = 'bar_chart', title, body, action, className = '' }: ChartEmptyProps) {
  return (
    <div className={`chart-empty ${className}`}>
      <Symbol name={icon} size={24} className="text-ink-400" />
      <b className="text-[14.5px] font-semibold text-ink-900">{title}</b>
      {body && <span className="text-[13.5px]">{body}</span>}
      {action && <div className="mt-1.5">{action}</div>}
    </div>
  );
}
